import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import useAuthenticationService from 'shared/api/services/authenticationService';
import { fetchSessionContext } from 'shared/api/sessionContext';
import {
  clearSessionProfile,
  getRegistryTenantIdFromSession,
  getSessionProfile,
} from 'shared/utils/sessionProfileStore';
import toast from 'react-hot-toast';
import { buildTenantLoginPath, getTenantIdFromWindowLocation, resolveTenantPublicPathKey } from 'shared/utils/tenantRoute';
import {
  getLastLoginTenantId,
  getLastLoginTenantSlug,
  setLastLoginTenantId,
  setLastLoginTenantSlug,
} from 'shared/utils/lastTenantLogin';
import { AUTH_SESSION_EXPIRED } from 'shared/utils/authSessionEvents';
import {
  startIdleSessionMonitor,
  stopIdleSessionMonitor,
  continueIdleSession,
  notifyIdleActivity,
} from 'shared/utils/idleSessionManager';
import {
  clearWorkState,
  installWorkSnapshotCollector,
} from 'shared/utils/sessionWorkState';
import { extractProfileUserId } from 'shared/api/sessionContext';
import {
  fetchSessionPolicy,
  postSessionActivity,
} from 'shared/api/sessionIdleApi';
import { postCookieRefresh } from 'shared/api/authRefresh';
import { getIdleSessionPolicy } from 'shared/utils/idleSessionConfig';
import SessionIdleWarningDialog from 'shared/components/SessionIdleWarningDialog';
import { setCachedPublicBranding } from 'shared/api/services/publicTenantBrandingService';

export const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  sessionLoading: false,
  refreshSessionProfile: async () => null,
  signIn: () => Promise.resolve({ ok: false, message: '' }),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  requestPasswordReset: () => Promise.resolve({ ok: false, message: '' }),
  verifyResetCode: () => Promise.resolve({ ok: false, message: '' }),
  resetPassword: () => Promise.resolve({ ok: false, message: '' }),
});

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const [idleRemainingSeconds, setIdleRemainingSeconds] = useState(60);
  const navigate = useNavigate();
  const idleCountdownRef = useRef(null);
  const signingOutRef = useRef(false);
  const {
    signIn: signInService,
    signUp: signUpService,
    logout: signOutService,
    requestPasswordResetService,
    verifyResetCodeService,
    resetPasswordService,
  } = useAuthenticationService();

  const resolvePublicTenantLoginPath = useCallback((tenantIdOverride = null) => {
    const routeTenantId = getTenantIdFromWindowLocation();
    const sessionTenantId = getRegistryTenantIdFromSession();
    const profile = getSessionProfile();
    const brandingName =
      profile?.tenantBranding?.name || getLastLoginTenantSlug();
    return buildTenantLoginPath(
      resolveTenantPublicPathKey({
        name: brandingName,
        id: tenantIdOverride || routeTenantId || sessionTenantId || getLastLoginTenantId(),
        fallback:
          tenantIdOverride ||
          routeTenantId ||
          getLastLoginTenantSlug() ||
          getLastLoginTenantId(),
      })
    );
  }, []);

  const refreshSessionProfile = useCallback(async () => {
    const profile = await fetchSessionContext();
    if (profile) {
      setUser({ data: profile });
      setIsAuthenticated(true);
      if (profile.tenantId) {
        setLastLoginTenantId(profile.tenantId);
      }
      if (profile.tenantBranding?.name) {
        setLastLoginTenantSlug(profile.tenantBranding.name);
      }
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
    return profile;
  }, []);

  const clearIdleCountdown = useCallback(() => {
    if (idleCountdownRef.current != null) {
      window.clearInterval(idleCountdownRef.current);
      idleCountdownRef.current = null;
    }
  }, []);

  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const persistWorkAndExpire = useCallback(async (reason = 'inactivity') => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    clearIdleCountdown();
    setIdleWarningOpen(false);
    stopIdleSessionMonitor();

    const profile = getSessionProfile() || userRef.current?.data;
    const userId = extractProfileUserId(profile);
    const tenantId =
      profile?.tenantId
      || getRegistryTenantIdFromSession()
      || getLastLoginTenantId()
      || getTenantIdFromWindowLocation();
    if (tenantId) {
      setLastLoginTenantId(tenantId);
    }
    // Keep branding/logo available for instant login paint after hard reload.
    if (profile?.tenantBranding) {
      setCachedPublicBranding(profile.tenantBranding, [
        tenantId,
        profile.tenantName,
        getTenantIdFromWindowLocation(),
      ].filter(Boolean));
    }
    // Discard any previous resume snapshot — login always starts fresh.
    if (userId) {
      clearWorkState(userId);
    }

    const redirectPath = resolvePublicTenantLoginPath(tenantId);
    const separator = redirectPath.includes('?') ? '&' : '?';
    const target = `${redirectPath}${separator}reason=${encodeURIComponent(reason)}`;

    clearSessionProfile();

    // Always leave the app promptly — do not wait on a hung logout API call.
    if (typeof window !== 'undefined' && window.location) {
      window.location.replace(target);
    }

    try {
      await Promise.race([
        signOutService(reason),
        new Promise((resolve) => {
          window.setTimeout(resolve, 2500);
        }),
      ]);
    } catch {
      /* redirect already started */
    }

    setUser(null);
    setIsAuthenticated(false);
  }, [
    clearIdleCountdown,
    resolvePublicTenantLoginPath,
    signOutService,
  ]);

  const persistWorkAndExpireRef = useRef(persistWorkAndExpire);
  useEffect(() => {
    persistWorkAndExpireRef.current = persistWorkAndExpire;
  }, [persistWorkAndExpire]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      await fetchSessionPolicy();
      const profile = await fetchSessionContext();
      if (cancelled) return;
      if (profile) {
        setUser({ data: profile });
        setIsAuthenticated(true);
        if (profile.tenantId) {
          setLastLoginTenantId(profile.tenantId);
        }
        if (profile.tenantBranding?.name) {
          setLastLoginTenantSlug(profile.tenantBranding.name);
        }
        if (profile.tenantBranding) {
          setCachedPublicBranding(profile.tenantBranding, [
            profile.tenantId,
            profile.tenantName,
            getTenantIdFromWindowLocation(),
          ].filter(Boolean));
        }
      } else {
        clearSessionProfile();
        setUser(null);
        setIsAuthenticated(false);
      }
      setSessionLoading(false);
    }
    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => installWorkSnapshotCollector(), []);

  useEffect(() => {
    const onSessionExpired = () => {
      // Same path as idle logout: save work and return to tenant login (not a fake 404).
      void persistWorkAndExpireRef.current('session-expired');
    };
    window.addEventListener(AUTH_SESSION_EXPIRED, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED, onSessionExpired);
  }, []);

  const idleWarningOpenRef = useRef(false);

  useEffect(() => {
    idleWarningOpenRef.current = idleWarningOpen;
  }, [idleWarningOpen]);

  useEffect(() => {
    if (!isAuthenticated) {
      stopIdleSessionMonitor();
      clearIdleCountdown();
      setIdleWarningOpen(false);
      signingOutRef.current = false;
      return undefined;
    }

    fetchSessionPolicy();

    const accessMs = Math.max(
      60_000,
      Number(getIdleSessionPolicy().accessTokenExpirationMs) || 30 * 60 * 1000
    );
    const refreshEveryMs = Math.max(30_000, accessMs - 90_000);
    const keepAliveId = window.setInterval(() => {
      postCookieRefresh().catch(() => {
        /* 401 interceptor / session-expired handler takes over if refresh is invalid */
      });
    }, refreshEveryMs);

    startIdleSessionMonitor({
      onWarning: ({ remainingMs }) => {
        const leadMs = Math.max(1000, remainingMs || 60_000);
        const deadline = Date.now() + leadMs;
        setIdleRemainingSeconds(Math.ceil(leadMs / 1000));
        setIdleWarningOpen(true);
        toast.error(
          'You have been idle. Your session will end in about one minute unless you stay signed in.',
          { id: 'idle-session-warning', duration: leadMs }
        );
        clearIdleCountdown();
        idleCountdownRef.current = window.setInterval(() => {
          const left = Math.ceil((deadline - Date.now()) / 1000);
          setIdleRemainingSeconds(Math.max(0, left));
          if (left <= 0) {
            clearIdleCountdown();
            void persistWorkAndExpireRef.current('inactivity');
          }
        }, 250);
      },
      onExpire: () => {
        toast.dismiss('idle-session-warning');
        void persistWorkAndExpireRef.current('inactivity');
      },
      onActivity: () => {
        toast.dismiss('idle-session-warning');
        if (idleWarningOpenRef.current) {
          clearIdleCountdown();
          setIdleWarningOpen(false);
        }
        postSessionActivity();
      },
    });

    return () => {
      window.clearInterval(keepAliveId);
      stopIdleSessionMonitor();
      clearIdleCountdown();
    };
  }, [isAuthenticated, clearIdleCountdown]);

  const handleStaySignedIn = useCallback(async () => {
    clearIdleCountdown();
    setIdleWarningOpen(false);
    continueIdleSession();
    notifyIdleActivity('continue');
    await postSessionActivity();
    toast.dismiss('idle-session-warning');
    toast.success('Session extended.');
  }, [clearIdleCountdown]);

  const handleLogoutFromWarning = useCallback(() => {
    void persistWorkAndExpireRef.current('user-logout');
  }, []);

  const signIn = useCallback(
    async (email, phoneNumber, password) => {
      try {
        const response = await signInService(email, phoneNumber, password);
        if (response.code === '0') {
          await fetchSessionPolicy();
          const profile = getSessionProfile();
          setUser(profile ? { data: profile } : null);
          setIsAuthenticated(Boolean(profile));
          if (profile?.tenantId) {
            setLastLoginTenantId(profile.tenantId);
          }
          if (profile?.tenantBranding?.name) {
            setLastLoginTenantSlug(profile.tenantBranding.name);
          }
          if (profile?.tenantBranding) {
            setCachedPublicBranding(profile.tenantBranding, [
              profile.tenantId,
              profile.tenantName,
              getTenantIdFromWindowLocation(),
            ].filter(Boolean));
          }
          signingOutRef.current = false;
          const message = response.message || 'Signed in successfully';
          return { ok: true, message };
        }
        toast.error(response.message || 'Sign-in failed');
        return { ok: false, message: response.message || 'Sign-in failed' };
      } catch (error) {
        const message = error?.message || 'Sign-in failed';
        toast.error(message);
        return { ok: false, message };
      }
    },
    [signInService]
  );

  const signUp = useCallback(
    async (userData) => {
      try {
        const response = await signUpService(userData);
        if (response.code === '0') {
          navigate(resolvePublicTenantLoginPath());
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error('AuthProvider SignUp Error:', error.message);
        toast.error(error.message);
      }
    },
    [navigate, resolvePublicTenantLoginPath, signUpService]
  );

  const signOut = useCallback(async (tenantIdOverride = null) => {
    try {
      const resolvedTenantId =
        tenantIdOverride ||
        user?.data?.tenantId ||
        getRegistryTenantIdFromSession() ||
        getLastLoginTenantId() ||
        getTenantIdFromWindowLocation();
      if (resolvedTenantId) {
        setLastLoginTenantId(resolvedTenantId);
      }
      if (user?.data?.tenantBranding) {
        setCachedPublicBranding(user.data.tenantBranding, [
          resolvedTenantId,
          user.data.tenantName,
          getTenantIdFromWindowLocation(),
        ].filter(Boolean));
      }
      const redirectPath = resolvePublicTenantLoginPath(resolvedTenantId);
      stopIdleSessionMonitor();
      clearIdleCountdown();
      setIdleWarningOpen(false);
      await signOutService();
      clearSessionProfile();
      if (typeof window !== 'undefined' && window.location) {
        window.location.replace(redirectPath);
        return;
      }
      setIsAuthenticated(false);
      setUser(null);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      throw new Error(error.message);
    }
  }, [clearIdleCountdown, navigate, resolvePublicTenantLoginPath, signOutService, user]);

  const requestPasswordReset = useCallback(
    async (email) => {
      try {
        const response = await requestPasswordResetService(email);
        if (response?.code === '0') {
          const message = response.message || 'Code is sent to your email account';
          toast.success(message);
          return { ok: true, message };
        }
        const message = response?.message || 'Error in sending code to your email account';
        toast.error(message);
        return { ok: false, message };
      } catch (error) {
        const message = error?.message || 'Error in sending code to your email account';
        toast.error(message);
        console.error(message);
        return { ok: false, message };
      }
    },
    [requestPasswordResetService]
  );

  const verifyResetCode = useCallback(
    async (email, code) => {
      try {
        const response = await verifyResetCodeService(email, code);
        if (response?.code === '0') {
          const message = response.message || 'Code is successfully verified';
          toast.success(message);
          return { ok: true, message };
        }
        const message = response?.message || 'Error in verifying code';
        toast.error(message);
        return { ok: false, message };
      } catch (error) {
        const message = error?.message || 'Error in verifying code';
        toast.error(message);
        console.error(message);
        return { ok: false, message };
      }
    },
    [verifyResetCodeService]
  );

  const resetPassword = useCallback(
    async (email, newPassword, confirmPassword) => {
      try {
        const response = await resetPasswordService(email, newPassword, confirmPassword);
        if (response?.code === '0') {
          const message = response.message || 'New password is saved successfully';
          toast.success(message);
          navigate(resolvePublicTenantLoginPath());
          return { ok: true, message };
        }
        const message = response?.message || 'Error in saving new password';
        toast.error(message);
        return { ok: false, message };
      } catch (error) {
        const message = error?.message || 'Error in saving new password';
        toast.error(message);
        console.error(message);
        return { ok: false, message };
      }
    },
    [navigate, resetPasswordService, resolvePublicTenantLoginPath]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        sessionLoading,
        refreshSessionProfile,
        signIn,
        signUp,
        signOut,
        requestPasswordReset,
        verifyResetCode,
        resetPassword,
      }}
    >
      {children}
      <SessionIdleWarningDialog
        open={idleWarningOpen}
        remainingSeconds={idleRemainingSeconds}
        onContinue={handleStaySignedIn}
        onLogoutNow={handleLogoutFromWarning}
      />
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
